/**
 * Terms of Service for Attractr + Audience Lab — June 2026 Legal
 * Framework supplied by the client (Akuntsu Inc.).
 *
 * EN is the canonical text. FR is being prepared by the client; until
 * it lands, the FR locale shows a short notice and renders the
 * authoritative EN copy so users still see the current terms.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { ExternalLink } from '../../components';
import classNames from 'classnames';

import css from './TermsOfService.module.css';
import { useIntl } from '../../util/reactIntl';

const EFFECTIVE_DATE = 'June 22, 2026';

const EnglishContent = () => (
  <>
    <p className={css.lastUpdated}>Effective Date: {EFFECTIVE_DATE}</p>

    <h2>1. Acceptance of Terms</h2>
    <p>
      These Terms of Service ("Terms") govern your access to and use of the Attractr marketplace
      platform ("Attractr") and the Audience Lab community forum and educational program ("Audience
      Lab"), collectively the "Services". The Services are owned and operated by Akuntsu Inc.
      Butterfly Innov provides exclusive development and technical services in support of the
      Services.
    </p>
    <p>
      By registering for an account, accessing, or using the Services, you agree to be bound by
      these Terms and our Privacy Policy. If you do not agree, you may not use the Services.
    </p>

    <h2>2. Eligibility</h2>
    <p>
      The Services are intended solely for users who are 18 years of age or older. By using the
      Services, you represent that you are at least 18 years old and have the legal capacity to
      enter into these Terms.
    </p>

    <h2>3. Account Registration</h2>
    <p>
      To access certain features, you may be required to register for an account. You agree to
      provide accurate, current, and complete information and to keep it up to date. You are
      responsible for maintaining the confidentiality of your account credentials and for all
      activities that occur under your account.
    </p>

    <h2>4. Intellectual Property</h2>
    <p>
      The Services and all content and materials are the property of Akuntsu or its licensors and
      are protected by copyright, trademark, and other intellectual property laws.
    </p>
    <p>
      As a Supplier on Attractr, you retain ownership of the content you create and share on the
      platform. By posting content to the Audience Lab or the Platform, you grant Akuntsu a
      worldwide, non-exclusive, royalty-free license to host, display, reproduce, and distribute
      such content solely for the purpose of operating and improving the Services.
    </p>
    <p>
      By default, your user content and personal data are not used to train our core artificial
      intelligence models (including Clara AI). Any such utilization strictly requires your
      separate, explicit, and affirmative opt-in consent.
    </p>

    <h2>5. Prohibited Conduct</h2>
    <ul>
      <li>Violate any applicable law or regulation.</li>
      <li>
        Share content that is unlawful, harmful, threatening, abusive, harassing, defamatory,
        vulgar, obscene, invasive of another's privacy, hateful, or discriminatory.
      </li>
      <li>Infringe any intellectual property rights of any party.</li>
      <li>Interfere with or disrupt the Services or networks connected to the Services.</li>
      <li>
        Use automated means to access the Services or collect information about users without our
        express written permission.
      </li>
      <li>Impersonate any person or entity.</li>
      <li>Engage in any fraudulent, deceptive, or illegal activity.</li>
      <li>Share content depicting or targeting individuals under the age of 18.</li>
      <li>
        Use bots, click-farms, or other manipulative tactics to inflate engagement metrics or
        influence Clara AI's cultural intelligence signals.
      </li>
      <li>Harass, bully, or discriminate against any individual or group.</li>
      <li>
        Spam other users with unsolicited commercial messages unrelated to the performing arts.
      </li>
    </ul>

    <h2>6. Payments and Refunds</h2>
    <p>
      All payments are processed through third-party payment processors, such as Stripe and
      WooCommerce. Refunds may be granted in limited circumstances, including duplicate payment,
      fraudulent transaction, cancellation of an event, or platform error. All refund requests must
      be submitted within thirty (30) days of the original transaction date.
    </p>

    <h2>7. Account Termination, Data Erasure, and Forum Continuity</h2>
    <p>
      Akuntsu may terminate or suspend your access to the Services at any time, without prior notice
      or liability, for any reason, including breach of these Terms. Upon termination, your right to
      use the Services will immediately cease. You will have 30 days to request an export of your
      content before it is permanently deleted.
    </p>
    <p>
      In strict compliance with international data protection standards and the statutory Right to
      be Forgotten, users may request the total deletion of their platform accounts and personal
      profiles at any time through the system dashboard.
    </p>
    <p>
      Upon execution of a valid account deletion request, all Personally Identifiable Information
      (PII) linked to the profile — including your legal name, professional handle, verification
      logs, email addresses, uploaded avatars, and billing records — will be permanently and
      irreversibly purged from our live database environments.
    </p>
    <p>
      However, to preserve the community history and conversational continuity of the Audience Lab
      public message boards, any textual posts, replies, and discussion threads you created prior to
      account deletion will remain visible to the network. These textual assets will be
      programmatically stripped of your personal identity and permanently reassigned to an anonymous
      system handle labeled [Anonymous User]. By participating in the platform forums, you grant
      Akuntsu Inc. the permanent, non-exclusive right to maintain such anonymized text strings to
      ensure ecosystem stability.
    </p>

    <h2>8. Disclaimer of Warranties</h2>
    <p>
      The Services are provided on an "as is" and "as available" basis, without any warranties of
      any kind, whether express or implied. The Clara AI engine provides predictive insights
      intended to support Suppliers, but does not guarantee specific commercial results. Clara AI
      does not replace professional, financial, legal, or strategic advice.
    </p>

    <h2>9. Limitation of Liability</h2>
    <p>
      To the fullest extent permitted by applicable law, in no event shall Akuntsu, its affiliates,
      officers, directors, employees, agents, or licensors be liable for any indirect, incidental,
      special, consequential, or punitive damages arising out of or related to your use of or
      inability to use the Services. In no event shall Akuntsu's total liability to you for all
      damages exceed the greater of (a) the amount paid by you to Akuntsu in the 6 months
      immediately preceding the event, or (b) $100 CAD.
    </p>

    <h2>10. Indemnification</h2>
    <p>
      You agree to indemnify, defend, and hold harmless Akuntsu, its affiliates, officers,
      directors, employees, agents, and licensors from and against any claims, liabilities, damages,
      and fees arising out of or related to your violation of these Terms or your use of the
      Services.
    </p>

    <h2>11. Legal Compliance Commitments</h2>
    <p>
      The following operational parameters are maintained in strict compliance with applicable law
      and cannot be altered without formal legal review:
    </p>
    <ul>
      <li>
        <strong>AI Consent Default:</strong> The checkbox permitting use of user data to train
        ClaraLive.ai remains unchecked by default at all times. Pre-checking or obscuring this
        consent mechanism in the Terms of Service is a direct violation of Quebec's Law 25.
      </li>
      <li>
        <strong>Invoicing and Payments:</strong> All payments to Butterfly Innov are strictly
        milestone-based. No fixed salary arrangements are maintained, in compliance with Serbian tax
        regulations.
      </li>
      <li>
        <strong>Ownership Representation:</strong> Akuntsu Inc. is the 100% sole owner and operator
        of all platform infrastructure, data, and intellectual property. No co-ownership, licensee,
        or partnership representations are made in any public documents, website footers, or press
        releases, in order to preserve eligibility for Canadian SR&amp;ED and CDAEIA tax subsidies.
      </li>
      <li>
        <strong>Refund Policy:</strong> Customer support adheres strictly to the defined refund
        triggers (duplicate payment, cancelled event, platform technical failure). No refunds are
        processed after 30 days of the original transaction.
      </li>
      <li>
        <strong>Language Hierarchy:</strong> The French version of all legal documents is always
        available and prevails for users in Quebec, in compliance with Quebec's Bill 96 and
        applicable regulations.
      </li>
    </ul>

    <h2>12. Governing Law and Jurisdiction</h2>
    <p>
      These Terms shall be governed by and construed in accordance with the laws of the Province of
      Quebec and the federal laws of Canada applicable therein. Any legal proceedings shall be
      instituted exclusively in the courts of the judicial district of Montreal, Quebec, Canada.
    </p>

    <h2>13. Dispute Resolution</h2>
    <p>
      If a dispute arises, the Parties agree to first try to resolve it informally. You must send
      written notice to{' '}
      <ExternalLink href="mailto:legal@attractr.live">legal@attractr.live</ExternalLink>. If the
      dispute is not resolved within 60 days after the initial notice, either party may commence
      formal proceedings.
    </p>

    <h2>14. Language</h2>
    <p>
      These Terms are available in both English and French. For users located in Quebec, the French
      version shall prevail where required by applicable law.
    </p>

    <h2>15. Changes to These Terms</h2>
    <p>
      Akuntsu reserves the right to modify these Terms at any time. If we make material changes, we
      will notify you by email or by posting a notice on the Services prior to the effective date.
      Your continued use of the Services after the effective date constitutes your acceptance of the
      modified Terms.
    </p>

    <h2>16. Contact Information</h2>
    <p>
      If you have any questions about these Terms, please contact:{' '}
      <ExternalLink href="mailto:legal@attractr.live">legal@attractr.live</ExternalLink>
    </p>
  </>
);

const FrenchPendingBanner = () => (
  <div className={css.translationNotice}>
    <p>
      <strong>Mise à jour en cours.</strong> La version française des présentes Conditions
      d'utilisation est en cours de traduction. La version anglaise ci-dessous est la version en
      vigueur jusqu'à publication de la traduction française officielle.
    </p>
  </div>
);

const TermsOfService = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.root, className);
  const isFrench = useIntl().locale === 'fr';

  return (
    <div className={classes}>
      {isFrench ? <FrenchPendingBanner /> : null}
      <EnglishContent />
    </div>
  );
};

TermsOfService.defaultProps = {
  rootClassName: null,
  className: null,
};

TermsOfService.propTypes = {
  rootClassName: PropTypes.string,
  className: PropTypes.string,
};

export default TermsOfService;
